const read_string = (value = '') => {
    return `${value || ''}`.trim();
};

const read_key = (value = '') => {
    return read_string(value).toLowerCase();
};

const array_values = (value) => {
    return Array.isArray(value) ? value : [];
};

const first_string = (values = []) => {
    return values.map((value) => read_string(value)).find((value) => value.length) || '';
};

const unique_strings = (values = []) => {
    return [...new Set(values.map((value) => read_string(value)).filter(Boolean))];
};

const known_offer_type_keys = ['website_creation', 'website_maintenance', 'marketing', 'creative', 'graphic', 'sass', 'website'];

const service_type_key_from_service_key = (serviceKey = '') => {
    const normalizedServiceKey = read_key(serviceKey).replace(/[\s-]+/g, '_');
    const knownTypeKey = known_offer_type_keys.find((typeKey) => normalizedServiceKey.startsWith(`${typeKey}_`));

    if (knownTypeKey) {
        return knownTypeKey;
    }

    return normalizedServiceKey.split('_')[0] || '';
};

const offer_has_website_maintenance_service = (offerItem = {}) => {
    const selectedServices = offerItem?.setupData?.selectedServices || {};

    return Object.keys(selectedServices).some((serviceKey) => {
        return selectedServices[serviceKey] === true && read_key(serviceKey).replace(/[\s-]+/g, '_').startsWith('website_maintenance_');
    });
};

const offer_type_key = (value = '', offerItem = {}) => {
    const typeKey = read_key(value);

    if (typeKey === 'graphic') {
        return 'creative';
    }

    if (typeKey === 'website') {
        return offer_has_website_maintenance_service(offerItem) ? 'website_maintenance' : 'website_creation';
    }

    return typeKey;
};

const selected_type_from_offer = (offerItem = {}) => {
    const selectedServices = offerItem?.setupData?.selectedServices || {};
    const serviceDerivedTypes = Object.keys(selectedServices)
        .filter((serviceKey) => selectedServices[serviceKey] === true)
        .map((serviceKey) => service_type_key_from_service_key(serviceKey));
    const selectedTypes = [
        offerItem?.selectedType,
        ...array_values(offerItem?.selectedTypes),
        ...array_values(offerItem?.typesData?.selectedTypes),
        ...serviceDerivedTypes
    ].map((typeValue) => offer_type_key(typeValue, offerItem)).filter(Boolean);

    return selectedTypes[0] || '';
};

const selected_types_from_offer = (offerItem = {}) => {
    const selectedType = selected_type_from_offer(offerItem);

    if (selectedType.length) {
        return [selectedType];
    }

    return [];
};

const offer_type_label = (typeKey = '') => {
    const typeLabels = {
        marketing: 'Personalised ads',
        creative: 'One-offs',
        graphic: 'One-offs',
        website_creation: 'Website creation',
        website_maintenance: 'Website maintenance',
        website: 'Website',
        sass: 'SASS'
    };

    return typeLabels[typeKey] || typeKey.replace(/_/g, ' ');
};

const offer_type_label_from_offer = (offerItem = {}) => {
    return offer_type_label(selected_type_from_offer(offerItem));
};

const offer_reference_from_offer = (offerItem = {}) => {
    return first_string([
        offerItem?.ofrId
    ]) || '—';
};

const offer_client_name_from_offer = (offerItem = {}) => {
    const ownerName = `${offerItem?.client?.ownerFirstname || ''} ${offerItem?.client?.ownerLastname || ''}`.trim();

    return first_string([
        offerItem?.client?.brand,
        offerItem?.client?.brandName,
        offerItem?.client?.internalName,
        offerItem?.niffler?.client?.brand,
        offerItem?.niffler?.client?.brandName,
        offerItem?.niffler?.client?.internalName,
        offerItem?.niffler?.sourceBrandName,
        ownerName,
        offerItem?.typesData?.clientName,
        offerItem?.typesData?.sourceBrandName,
        offerItem?.clientName,
        offerItem?.setupData?.clientName
    ]) || '—';
};

const offer_emails_from_offer = (offerItem = {}) => {
    return unique_strings([
        ...array_values(offerItem?.client?.emails),
        ...array_values(offerItem?.clientEmails),
        ...array_values(offerItem?.typesData?.clientEmails)
    ]);
};

const offer_numbers_from_offer = (offerItem = {}) => {
    return unique_strings([
        ...array_values(offerItem?.client?.numbers),
        offerItem?.nifflerBrandPhone,
        ...array_values(offerItem?.nifflerBrandPhones),
        offerItem?.typesData?.contactPhone,
        ...array_values(offerItem?.typesData?.contactPhones)
    ]);
};

const offer_visits_from_offer = (offerItem = {}) => {
    if (array_values(offerItem?.activity?.visits).length) {
        return array_values(offerItem.activity.visits);
    }

    if (array_values(offerItem?.visits).length) {
        return array_values(offerItem.visits);
    }

    return array_values(offerItem?.visitHistory).map((visitItem) => {
        return {
            visitorId: visitItem?.visitorId || '',
            ip: visitItem?.ip || visitItem?.ipAddress || '',
            userAgent: visitItem?.userAgent || '',
            at: visitItem?.at || visitItem?.visitedAt || ''
        };
    });
};

const offer_visit_count = (offerItem = {}) => {
    const visits = offer_visits_from_offer(offerItem);

    if (visits.length) {
        return visits.length;
    }

    return Number(offerItem?.visitStats?.totalVisits || 0);
};

const offer_current_status = (offerItem = {}) => {
    const rootStatus = read_key(offerItem?.status);
    const isUnsentDraft = rootStatus === 'draft' || rootStatus === 'ready_for_review';
    const statusline = array_values(offerItem?.statusline).filter((timelineItem) => {
        return !(isUnsentDraft && read_key(timelineItem?.status) === 'valid');
    });

    if (statusline.length) {
        return read_key(statusline[statusline.length - 1]?.status);
    }

    const timeline = array_values(offerItem?.timeline).filter((timelineItem) => {
        return read_key(timelineItem?.status).length && !read_key(timelineItem?.note).length;
    });

    if (timeline.length) {
        return read_key(timeline[timeline.length - 1]?.status);
    }

    const currentStatusByBoard = offerItem?.currentStatusByBoard || {};
    const boardStatus = ['sales', 'marketing', 'website', 'creative']
        .map((boardKey) => read_key(currentStatusByBoard?.[boardKey]))
        .filter((statusKey) => !(isUnsentDraft && statusKey === 'valid'))
        .find((statusKey) => statusKey.length);

    return boardStatus || rootStatus;
};

const offer_deleted = (offerItem = {}) => {
    return offerItem?.deleted === true
        || offerItem?.active === false
        || read_string(offerItem?.deletedAt).length > 0;
};

export {
    selected_type_from_offer,
    selected_types_from_offer,
    offer_type_label,
    offer_type_label_from_offer,
    offer_reference_from_offer,
    offer_client_name_from_offer,
    offer_emails_from_offer,
    offer_numbers_from_offer,
    offer_visits_from_offer,
    offer_visit_count,
    offer_current_status,
    offer_deleted
};
